import React, { FC, Fragment, useMemo, useState } from 'react';
import { ContentGrid } from '@shared-tracpage/components/content-grid';
import { HomeWrapperItems } from '@shared-tracpage/components/home-wrapper-items';
import { List } from '@shared-tracpage/components/list';
import AppState from '@webapp/store/state';
import { TourEvent } from '@common/types/traclink/tour-event';
import { connect } from 'react-redux';
import { TourItemAlt } from '@shared-tracpage/components/tour-item-alt';
import { groupEventsByVisibility } from '@shared-tracpage/utils/group-by-visibility';
import TracPage from '@services/tracpage/types/trac-page';
import { TracpageNewTour } from '@components/tracpage/tour-event/tracpage-new-tour';
import { TracpageTourForm } from '@components/tracpage/tour-event/tracpage-tour-form';
import { CreateEventDraft } from '@webapp/store/traclink/action-creators';
import { Dispatch } from 'redux';
import { NewTourEvent } from '@common/types/traclink/new-tour-event';
import { v4 as uuid } from 'uuid';

type StateProps = {
  pageData: TracPage;
  events: TourEvent[];
};
type DispatchProps = {
  createEventDraft: (
    ...params: ConstructorParameters<typeof CreateEventDraft>
  ) => CreateEventDraft;
};
type Props = StateProps & DispatchProps;

const mapStateToProps = (state: AppState): StateProps => ({
  events: [
    ...state.traclink.tourEventDrafts.new,
    ...state.traclink.tourEvents.map(
      (te) => state.traclink.tourEventDrafts.edit[te.id] ?? te
    ),
  ],
  pageData: state.tracpage.artistPageDraft,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  createEventDraft: (event, draftKey) =>
    dispatch(new CreateEventDraft(event, draftKey)),
});

const TourPage: FC<Props> = ({ events, pageData, createEventDraft }) => {
  const [editEventId, setEditEventId] = useState<string>();

  const { visible, hidden } = useMemo(
    () => groupEventsByVisibility(events, pageData),
    [events, pageData]
  );

  const handleAdd = (newTour: Omit<NewTourEvent, 'ownerId'>) => {
    createEventDraft(
      {
        id: uuid(),
        created_at: new Date(),
        updated_at: new Date(),
        ownerId: '',
        ...newTour,
      },
      'new'
    );
  };

  const handleEdit = (tour: TourEvent) => {
    createEventDraft(tour, 'edit');
    setEditEventId(undefined);
  };

  return (
    <>
      <HomeWrapperItems>
        <ContentGrid noEditUI title="Events">
          <TracpageNewTour
            onAdd={handleAdd}
            isLight={pageData.theme === 'light'}
          />
          <List gap="small">
            {visible.map((e, idx, self) => (
              <Fragment key={e.id}>
                {e.id === editEventId ? (
                  <TracpageTourForm
                    isLight={pageData.theme === 'light'}
                    onCancel={() => setEditEventId(undefined)}
                    variant="edit"
                    formProps={{
                      onEdit: handleEdit,
                      tour: e,
                    }}
                  />
                ) : (
                  <TourItemAlt
                    orderButtonsDisabledState={{
                      up: idx === 0,
                      down: idx === self.length - 1,
                    }}
                    id={e.id}
                    title={e.title}
                    location={e.place}
                    date={new Date(e.dateTime || '')}
                    onClick={() => setEditEventId(e.id)}
                  />
                )}
              </Fragment>
            ))}
          </List>
        </ContentGrid>
        {!!hidden.length && (
          <ContentGrid noEditUI title="Hidden Events">
            <List gap="small">
              {hidden.map((e) => (
                <TourItemAlt
                  isHidden
                  key={e.id}
                  id={e.id}
                  title={e.title}
                  location={e.place}
                  date={new Date(e.dateTime || '')}
                />
              ))}
            </List>
          </ContentGrid>
        )}
      </HomeWrapperItems>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TourPage);
